import { safeAsync } from '@/helpers/errors/safeAsync';

/**
 * Helper function to handle missing queryFn error from connect-query when trying to access the queryClient in an interceptor.
 * This is useful for interceptors that need to access the queryClient to get some data (e.g. auth token, app version, etc.).
 * It provides a more descriptive error message and a hint on how to fix it.
 */
export async function missingQueryFnHandler<T>(promise: Promise<T>) {
  const [error, data] = await safeAsync(promise, (e: { message?: string }) => e);
  if (error) {
    if (error.message?.includes('Missing queryFn')) {
      const errorMessage = [
        "Interceptor: 'queryFn' is not provided, or not previously called with the same 'queryKey'.",
      ];
      if (process.env.NODE_ENV === 'test') {
        errorMessage.push(
          '... To facilitate things, you should mock the interceptors (if appropriate) at the top of ' +
            "your test file with `vi.mock('@/api/interceptors')` to skip things like authorization logic.",
        );
      }
      throw new Error(errorMessage.join('\n'), { cause: error });
    }
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw error;
  }
  return data;
}
